import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

import BiographyContent from './BiographyContent';
import ContentLayout from './ContentLayout';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getBannerHeight(viewportHeight) {
  const rootFontSize = Number.parseFloat(
    window.getComputedStyle(document.documentElement).fontSize,
  ) || 16;

  return clamp(
    viewportHeight * 0.204,
    rootFontSize * 8.4,
    rootFontSize * 13.8,
  );
}

function getHeroMetrics() {
  const viewportHeight = window.visualViewport?.height || window.innerHeight;
  const bannerHeight = getBannerHeight(viewportHeight);
  const shrinkDistance = Math.max(viewportHeight - bannerHeight, 1);
  const scrollTop = window.scrollY;
  const progress = clamp(scrollTop / shrinkDistance, 0, 1);
  const height = viewportHeight - ((viewportHeight - bannerHeight) * progress);

  return {
    bannerHeight,
    height,
    progress,
    shrinkDistance,
    scrollTop,
  };
}

function Home() {
  const rootRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let frameId = 0;

    const updateHero = () => {
      frameId = 0;
      const root = rootRef.current;

      if (!root) {
        return;
      }

      const {
        bannerHeight,
        height,
        progress,
        scrollTop,
        shrinkDistance,
      } = getHeroMetrics();

      root.style.setProperty('--home-hero-banner-height', `${bannerHeight.toFixed(2)}px`);
      root.style.setProperty('--home-hero-height', `${height.toFixed(2)}px`);
      root.style.setProperty('--home-hero-progress', progress.toFixed(4));
      root.classList.toggle('home-page-hero-collapsed', progress > 0.82);
      root.classList.toggle('home-page-hero-released', scrollTop >= shrinkDistance);
    };

    const scheduleUpdate = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(updateHero);
    };

    updateHero();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.visualViewport?.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.visualViewport?.removeEventListener('resize', scheduleUpdate);

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let timeoutId = 0;
    let hashFrameId = 0;

    const scrollToHashBiography = () => {
      if (!['#biography', '#home-biography'].includes(window.location.hash)) {
        return;
      }

      if (window.location.hash === '#home-biography') {
        window.history.replaceState(null, '', '#biography');
      }

      window.clearTimeout(timeoutId);

      if (hashFrameId) {
        window.cancelAnimationFrame(hashFrameId);
      }

      timeoutId = window.setTimeout(() => {
        hashFrameId = window.requestAnimationFrame(() => {
          const target = document.getElementById('biography');

          if (target) {
            target.scrollIntoView({ block: 'start', behavior: 'auto' });
          }
        });
      }, 0);
    };

    scrollToHashBiography();
    window.addEventListener('hashchange', scrollToHashBiography);

    return () => {
      window.removeEventListener('hashchange', scrollToHashBiography);
      window.clearTimeout(timeoutId);

      if (hashFrameId) {
        window.cancelAnimationFrame(hashFrameId);
      }
    };
  }, []);

  const scrollToBiography = (event) => {
    if (typeof window === 'undefined') {
      return;
    }

    event.preventDefault();
    window.history.pushState(null, '', '#biography');
    const target = document.getElementById('biography');
    const targetTop = target
      ? target.getBoundingClientRect().top + window.scrollY
      : getHeroMetrics().shrinkDistance;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: targetTop,
      left: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div
      className="home-page"
      ref={rootRef}
    >
      <section className="home-hero-stage" aria-label="John Malam">
        <div className="home-hero">
          <img
            className="home-hero-image"
            src={`${process.env.PUBLIC_URL}/john.png`}
            alt="John Malam"
          />

          <div className="home-hero-overlay">
            <h1>John Malam</h1>
            <p>Best-selling children's author from Cheshire, north-west England</p>
            <nav className="home-hero-nav" aria-label="Primary navigation">
              <a href="#biography" onClick={scrollToBiography}>Biography</a>
              <Link to="/books">Books</Link>
              <Link to="/malamfamilyhistory">Malam Family History</Link>
              <Link to="/contact">Contact</Link>
            </nav>
          </div>
        </div>
      </section>

      <section id="biography" className="home-biography-layout">
        <ContentLayout
          title="Biography"
          intro="Best-selling children's non-fiction author from Cheshire, north-west England."
          hideHeroBanner
          navigationPath="/about"
          className="home-embedded-content"
        >
          <BiographyContent />
        </ContentLayout>
      </section>
    </div>
  );
}

export default Home;
